import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useColorScheme, useTheme } from "@mui/material/styles";

import {
  DataTable,
  Disclose,
  EmptyState,
  ErrorState,
  KernelIcon,
  KernelLockup,
  KernelMark,
  Loading,
  Permission,
  Provenance,
  ReachableBy,
  RecordGraph,
  Refusal,
  Tooltip,
  hostConfig,
} from "@lopie/kernel-ui";
import {
  CATEGORIES,
  ORDER,
  RULES,
  SOURCE,
  SUMMARY,
} from "@lopie/kernel-ui/design-rules";

/**
 * The design system, saying what it actually enforces.
 *
 * There were rules and a theme and no way to see either. The rules were prose
 * in a README that only an agent ever read, through the MCP; the components
 * were four files nobody could look at without checking out the repo and
 * running a dev server; and the logo existed only as base64 for Cognito's
 * branding designer, which meant a micro-app author had nothing to render.
 *
 * ## The part that matters is the enforcement column
 *
 * A design system that labelled its own prose "enforced" would be making
 * exactly the overstatement this product exists to prevent, applied to
 * itself, which would be the most embarrassing possible place to make it. So
 * every rule carries what actually holds it up, and for half of them the
 * honest answer is "a reviewer, or nobody".
 *
 * The label is **not** a list in this file. It is the `**Enforced by**` line
 * under each heading in `packages/kernel-ui/README.md`, parsed by
 * `@lopie/kernel-ui/rules`, which is the same file `mcp/lopie_mcp/docs.py`
 * parses for the `design_rules` tool. A rule added once appears here and
 * there with no code change in either — the same test the reader app is held
 * to against `GET /schema`, applied to prose.
 *
 * ## The gallery is fabricated and says so
 *
 * Every record in the component gallery is invented. On a governance console
 * that is dangerous enough to warrant the banner it gets: a screen showing
 * fake rows next to seven screens showing real ones is a screen that could be
 * misread once, and once is enough.
 */

const TABS = [
  { id: "rules", label: "Rules" },
  { id: "brand", label: "Brand" },
  { id: "components", label: "Components" },
  { id: "mcp", label: "What the MCP serves" },
];

function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

/** A titled block with a sentence about what it is for. */
function Panel({ title, caption, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, ...sx }}>
      <Typography variant="h2" sx={{ mb: caption ? 0.5 : 1.5 }}>
        {title}
      </Typography>
      {caption ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {caption}
        </Typography>
      ) : null}
      {children}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

/**
 * What enforces a rule, as a word and a colour and never only a colour.
 *
 * `kernel` gets the permitted green because it is the only one of the three
 * that is a guarantee. `review` gets amber, which on this console means
 * *needs attention* — and a rule nothing but a reviewer holds up is exactly
 * that. Nothing here is red: prose is not a failure, it is a smaller claim.
 */
function EnforcedChip({ id, unlabelled }) {
  if (unlabelled) {
    return (
      <Tooltip title="No `**Enforced by**` line under this heading in the README. Not the same as `review` — nobody has said either way.">
        <Chip
          size="small"
          variant="outlined"
          label="unlabelled"
          sx={{ color: "kernel.refused", borderColor: "kernel.refused" }}
        />
      </Tooltip>
    );
  }
  const category = CATEGORIES[id];
  return (
    <Tooltip title={category.detail}>
      <Chip
        size="small"
        variant="outlined"
        label={category.label}
        sx={{ color: category.colour, borderColor: category.colour }}
      />
    </Tooltip>
  );
}

/**
 * Markdown, rendered enough.
 *
 * The reasoning under each rule is prose with inline code, bold, and the odd
 * fenced block. A markdown library is 40 kB to render six paragraphs, and
 * this console already carries 182 kB gzipped. So: paragraphs, fences, and
 * inline `code`/`**bold**`/`*italic*` — and anything it does not understand
 * still reaches the reader as its own characters, which is the same promise
 * `docs.py` makes about the parsing on the other side.
 */
function Prose({ markdown }) {
  const blocks = useMemo(() => splitBlocks(markdown), [markdown]);
  return (
    <Stack spacing={1.25}>
      {blocks.map((block, index) =>
        block.code ? (
          <Box
            key={index}
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.default",
              border: 1,
              borderColor: "divider",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: "0.75rem",
              // A code block is as wide as it is, and the page body never
              // scrolls sideways.
              overflowX: "auto",
            }}
          >
            {block.text}
          </Box>
        ) : (
          <Typography key={index} variant="body2" color="text.secondary">
            {inline(block.text)}
          </Typography>
        ),
      )}
    </Stack>
  );
}

function splitBlocks(markdown) {
  const out = [];
  const parts = String(markdown || "").split(/```[a-zA-Z]*\n/);
  parts.forEach((part, index) => {
    if (index % 2 === 1) {
      const [code, ...rest] = part.split("```");
      out.push({ code: true, text: code.replace(/\n$/, "") });
      const tail = rest.join("```").trim();
      if (tail) out.push(...paragraphs(tail));
      return;
    }
    out.push(...paragraphs(part));
  });
  return out;
}

function paragraphs(text) {
  return String(text)
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => ({ code: false, text: p.replace(/\n/g, " ") }));
}

/** `code`, **bold** and *italic*, and nothing else. */
function inline(text) {
  const out = [];
  const pattern = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      out.push(
        <Box
          key={match.index}
          component="code"
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.8125em",
            overflowWrap: "anywhere",
          }}
        >
          {match[1]}
        </Box>,
      );
    } else if (match[2] !== undefined) {
      out.push(<strong key={match.index}>{match[2]}</strong>);
    } else {
      out.push(<em key={match.index}>{match[3]}</em>);
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** First letter raised, and nothing else touched. */
function sentenceCase(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/**
 * How much is behind the control, counted rather than guessed.
 *
 * A `Disclose` whose count is a made-up number is worse than no count: it is
 * a promise about how much a reader is about to be given. `splitBlocks` is
 * the same splitter `Prose` renders with, so the number is of the blocks that
 * will actually appear.
 */
function countParagraphs(markdown) {
  return splitBlocks(markdown || "").length;
}

function Rules() {
  return (
    <Stack spacing={2}>
      <Panel
        title="What holds each of these up"
        caption={
          "Every rule below says what enforces it, and the label is read out " +
          "of the same line in the same file the MCP reads. There is no " +
          "second list — a design system keeping a private table of which of " +
          "its own rules are real would be the drift it exists to stop."
        }
      >
        <Stack spacing={1.5}>
          {ORDER.map((id) => (
            <Stack
              key={id}
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{ alignItems: { sm: "baseline" } }}
            >
              <Box sx={{ minWidth: 160 }}>
                <EnforcedChip id={id} />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                {CATEGORIES[id].detail}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
              >
                {SUMMARY[id]} of {SUMMARY.total}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2">
          {/* The uncomfortable number, said plainly and first. */}
          <strong>
            {SUMMARY.reviewOnly} of {SUMMARY.total} of these rules are enforced
            by nothing but a reviewer.
          </strong>{" "}
          {SUMMARY.kernel} have a part the kernel refuses outright and{" "}
          {SUMMARY.test} have a part a test catches. Most are enforced in
          half, and the sentence beside each label says which half — because
          treating a partly-enforced rule as enforced is the specific mistake
          these rules are about.
        </Typography>
        {SUMMARY.unlabelled ? (
          <Typography variant="body2" sx={{ mt: 1, color: "kernel.refused" }}>
            {SUMMARY.unlabelled} rule
            {SUMMARY.unlabelled === 1 ? " has" : "s have"} no label at all.
            That is a gap in the README, not a category.
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Source: <code>{SOURCE}</code>. Parsed, not restated — a rule added
          there appears here and in the MCP&apos;s <code>design_rules</code>{" "}
          with no change to either.
        </Typography>
      </Panel>

      {RULES.map((rule) => (
        <Paper key={rule.id} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {rule.rule}
          </Typography>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{ flexWrap: "wrap", rowGap: 0.75, mb: 1 }}
          >
            {rule.enforcement.unlabelled ? (
              <EnforcedChip unlabelled />
            ) : (
              rule.enforcement.by.map((id) => <EnforcedChip key={id} id={id} />)
            )}
          </Stack>

          {rule.enforcement.detail ? (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {/* The README writes this as a continuation of "**Enforced by**
                  `kernel` — …", so it starts lowercase there and reads
                  correctly. Standing on its own under a row of chips it reads
                  as a typo, so the first letter is raised here. Presentation
                  only: the sentence itself is never edited, because the
                  sentence is the source of record. */}
              {inline(sentenceCase(rule.enforcement.detail))}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: 1.5, color: "kernel.refused" }}>
              Nobody has written down what enforces this. That is different
              from &ldquo;a reviewer&rdquo;, and this screen will not guess.
            </Typography>
          )}

          <Divider sx={{ mb: 1.5 }} />

          {/* The reasoning is three to six paragraphs and there are ten
              rules, which is how this tab came to be 4,348 pixels of prose —
              a design screen breaking the rule it renders one card further
              down. **High level first, the detail one click in.**

              The summary is not written here. `Enforced by` already states
              the rule's own one-sentence version of itself, above, and the
              body is what that sentence is short for. So the control says
              how many paragraphs are behind it and the sentence stays where
              it was, which is the difference between collapsing a rule and
              paraphrasing one. */}
          <Disclose
            align="row"
            label="Why"
            count={countParagraphs(rule.reasoning)}
          >
            <Prose markdown={rule.reasoning} />
          </Disclose>
        </Paper>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Brand
// ---------------------------------------------------------------------------

/**
 * One colour, with the value it actually resolved to.
 *
 * Reading the computed value back rather than printing the hex from the
 * theme object is the point. A swatch that printed `#1f7a3d` under a square
 * that was rendering `#4fb474` would be a screen lying about the one thing it
 * exists to show, and the dark column below is drawn by stamping a data
 * attribute and trusting MUI's generated CSS to follow — which is precisely
 * the thing that silently did not work in LO-31.
 *
 * So the panel proves itself: if the two columns ever showed the same value,
 * it would be visible rather than plausible.
 */
function Swatch({ token, name }) {
  const ref = useRef(null);
  const [resolved, setResolved] = useState("");
  // The page's own mode is a dependency even though these two columns are
  // stamped: if the attribute ever stopped applying, the values would follow
  // the page instead, and this is what makes that visible rather than
  // plausible.
  const { mode, systemMode } = useColorScheme();

  useEffect(() => {
    if (!ref.current) return;
    setResolved(getComputedStyle(ref.current).backgroundColor);
  }, [token, mode, systemMode]);

  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
      <Box
        ref={ref}
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1,
          flexShrink: 0,
          bgcolor: token,
          border: 1,
          borderColor: "divider",
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            overflowWrap: "anywhere",
          }}
        >
          {resolved || "—"}
        </Typography>
      </Box>
    </Stack>
  );
}

/**
 * The same swatches under both schemes, side by side.
 *
 * `data-light` / `data-dark` is what `colorSchemeSelector: "data"` emits, and
 * the CSS variables are inherited, so stamping the attribute on a `div` gives
 * that subtree the other palette. It is the real generated CSS rather than a
 * second copy of the colours — which is the only version of this panel worth
 * having, since a hardcoded pair of hex columns would still look right on the
 * day the theme changed.
 */
function Scheme({ scheme, tokens }) {
  const attribute = scheme === "dark" ? { "data-dark": "" } : { "data-light": "" };
  return (
    <Box
      {...attribute}
      sx={{
        flex: 1,
        minWidth: 240,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Label>{scheme}</Label>
      <Stack spacing={1.25} sx={{ mt: 1 }}>
        {tokens.map(({ token, name }) => (
          <Swatch key={token} token={token} name={name} />
        ))}
      </Stack>
    </Box>
  );
}

function Brand() {
  const theme = useTheme();

  // From the theme, not from a list in this file. A semantic state added to
  // `palette.kernel` appears here with no change to this screen — the same
  // rule the tables follow against `/schema`.
  const semantic = useMemo(
    () =>
      Object.keys(theme.vars?.palette?.kernel || {}).map((name) => ({
        token: `kernel.${name}`,
        name,
      })),
    [theme],
  );

  const structural = [
    { token: "primary.main", name: "primary.main" },
    { token: "primary.light", name: "primary.light" },
    { token: "primary.dark", name: "primary.dark" },
    { token: "background.default", name: "background.default" },
    { token: "background.paper", name: "background.paper" },
    { token: "divider", name: "divider" },
  ];

  return (
    <Stack spacing={2}>
      <Panel
        title="The mark"
        caption={
          "Inline SVG in currentColor, so one asset is correct on a near-white " +
          "and a near-black ground. Cognito's sign-in page needs its own " +
          "base64 copies with the colour baked in — those are generated from " +
          "these files, one way, and the direction is written down in " +
          "packages/kernel-ui/src/brand/README.md."
        }
      >
        <Stack
          direction="row"
          spacing={4}
          sx={{ alignItems: "flex-end", flexWrap: "wrap", rowGap: 3 }}
        >
          <Box>
            <Label>KernelMark</Label>
            <KernelMark height={40} />
          </Box>
          <Box>
            <Label>KernelIcon</Label>
            <Stack direction="row" spacing={2} sx={{ alignItems: "flex-end" }}>
              <KernelIcon size={40} title="Kernel" />
              <KernelIcon size={24} />
              <KernelIcon size={16} />
            </Stack>
          </Box>
          <Box>
            <Label>KernelLockup</Label>
            <KernelLockup height={24} />
          </Box>
          <Box>
            <Label>inherits colour</Label>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <KernelIcon size={32} sx={{ color: "text.primary" }} />
              <KernelIcon size={32} sx={{ color: "kernel.attention" }} />
              <KernelIcon size={32} sx={{ color: "text.secondary" }} />
            </Stack>
          </Box>
        </Stack>
      </Panel>

      <Panel
        title="Semantic states"
        caption={
          "Named for what they mean, not for what colour they are. A screen " +
          "writing success.main for a permitted tag is a screen that will " +
          "eventually use success.main for something else and quietly " +
          "overload it — so the brand is teal and these five are left alone. " +
          "The names come from the theme, so a state added there appears here."
        }
      >
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          <Scheme scheme="light" tokens={semantic} />
          <Scheme scheme="dark" tokens={semantic} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
          Each value under a swatch is read back with{" "}
          <code>getComputedStyle</code> after it rendered — not printed from
          the theme object. A panel about colour that showed a hex the browser
          was not using would be the exact failure it is here to catch: in
          LO-31 the toggle reported <code>dark</code> and not one CSS variable
          moved.
        </Typography>
      </Panel>

      <Panel
        title="Structure"
        caption="Ground, paper, dividers and the teal accent, in both schemes."
      >
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          <Scheme scheme="light" tokens={structural} />
          <Scheme scheme="dark" tokens={structural} />
        </Stack>
      </Panel>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * Fabricated inputs, and the banner that says so.
 *
 * Ids are `example-…` rather than anything that looks like a kernel id, and
 * the tags are the ones this org really uses so the components render the
 * shapes they will actually meet. Every one of these is invented.
 */
const FAKE_ERROR = {
  status: 403,
  error: "not_in_manifest",
  parameter: "tagged",
  message: "the app does not declare 'payroll' on Person",
  isManifest: true,
  body: {
    error: "not_in_manifest",
    parameter: "tagged",
    type: "Person",
    tag: "payroll",
    detail:
      "the app does not declare 'payroll' on Person; it declares " +
      "['customer', 'employee', 'vendor']",
  },
};

const FAKE_TRANSPORT = {
  status: 0,
  message: "Failed to fetch",
  body: {},
  isManifest: false,
};

const FAKE_PARAMETER = {
  status: 400,
  error: "bad_cursor",
  parameter: "cursor",
  message: "this cursor was issued for a different collection",
  body: { error: "bad_cursor", parameter: "cursor" },
  isManifest: false,
};

const FAKE_ROWS = [
  { id: "example-0001", tags: ["customer"], name: "Northwind Trading", total: 212500 },
  { id: "example-0002", tags: [], name: "Ridgeway Holdings", total: 8925 },
  { id: "example-0003", tags: ["customer", "vendor"], name: "Delacroix & Sons Limited", total: 1043075 },
];

const FAKE_COLUMNS = [
  { key: "id", label: "Kernel id", mono: true },
  {
    key: "tags",
    label: "Tags",
    render: (row) =>
      row.tags.length ? (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
          {row.tags.map((t) => (
            <Chip key={t} size="small" variant="outlined" label={t} />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          untagged
        </Typography>
      ),
  },
  { key: "name", label: "name" },
  {
    key: "total",
    label: "total",
    align: "right",
    render: (row) => (row.total / 100).toFixed(2),
  },
];

const FAKE_RECORD = {
  id: "example-0003",
  tags: ["customer", "vendor"],
  scope: "org",
  source: { system: "xero", id: "3f2a-…-91c" },
  fields: { name: "Delacroix & Sons Limited", total: 1043075 },
  provenance: {
    actor: "connector:xero",
    on_behalf_of: null,
    mode: "unattended",
    action: "sync.organization.upsert",
    at: "2026-09-08T04:15:22Z",
    source: "xero",
  },
};

const FAKE_FIELDS = [
  { name: "name" },
  { name: "total", minor_units: true },
];

const FAKE_GRAPH = {
  relationships: [
    {
      name: "people",
      target: "Person",
      permitted: true,
      edges: 46,
      sampled: true,
      nodes: [
        { id: "example-p1", label: "Mary Phillips", named: true },
        { id: "example-p2", label: "Adeola Bakare", named: true },
        { id: "example-p3", label: "example-p3", named: false },
      ],
    },
    {
      name: "invoices",
      target: "Invoice",
      permitted: false,
      reason: "not_in_manifest",
      detail: "this app does not declare Invoice",
      edges: null,
      nodes: [],
    },
  ],
};

const FAKE_APPS = [
  { app: "console", name: "Kernel console", can_read: true, reason: "tags_declared", owner: "platform" },
  { app: "reader", name: "Reader", can_read: false, reason: "tag_not_declared", owner: "platform" },
  { app: "case-notes", name: "Case notes", can_read: false, reason: "type_not_declared" },
];

function Demo({ title, why, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {why}
      </Typography>
      {children}
    </Paper>
  );
}

function Components() {
  return (
    <Stack spacing={2}>
      {/* Loud, and at the top. A governance console showing invented records
          beside seven screens showing real ones could be misread once, and
          once is enough.

          A plain Alert rather than ErrorState: nothing has gone wrong, and
          reaching for the error component because it happens to look right
          is how a component's meaning gets overloaded — which is the same
          argument the theme makes about `success.main`. */}
      <Alert severity="warning" variant="outlined">
        <AlertTitle>Everything below is fabricated</AlertTitle>
        <Typography variant="body2">
          No component on this tab has touched your kernel. The ids are{" "}
          <code>example-…</code>, the counts are made up, and the refusal is a
          hand-written object rather than a 403 anything returned. This is a
          gallery of what each component does — including the failure states,
          which are the ones people get wrong because they are the ones nobody
          sees while building.
        </Typography>
      </Alert>

      <Demo
        title="Refusal — the app was never allowed to ask"
        why="403 not_in_manifest. A fact about a declaration somebody wrote down, rendered in the kernel's own words, with the one sentence the kernel cannot supply: which document to change."
      >
        <Refusal failure={FAKE_ERROR} actor="This console" />
      </Demo>

      <Demo
        title="EmptyState — the request succeeded and matched nothing"
        why="The same screen, one letter of difference in the outcome, and an entirely different sentence. Rendering both of these as 'no results' throws away the only part a reader could act on."
      >
        <Stack spacing={1.5}>
          <EmptyState />
          <EmptyState tagged="customer" />
        </Stack>
      </Demo>

      <Demo
        title="ErrorState — not a permission"
        why="A network that did not answer, and a 400 that named the argument. Retry is offered on the first and would be offered on neither if this were a refusal — a retry button on a 403 invites four clicks before anybody reads the sentence."
      >
        <Stack spacing={1.5}>
          <ErrorState failure={FAKE_TRANSPORT} onRetry={() => {}} />
          <ErrorState failure={FAKE_PARAMETER} />
        </Stack>
      </Demo>

      <Demo
        title="Loading"
        why="A labelled spinner at a height that does not move the page when it resolves. The label is not decoration: a bare CircularProgress announces 'progress bar' to a screen reader and nothing else."
      >
        <Loading label="Reading the schema" minHeight={140} />
      </Demo>

      <Demo
        title="DataTable"
        why="Columns as data, its own scroll container, tabular-nums on anything right-aligned. The total column is minor units divided by 100 — 1043075 is 10,430.75, and a renderer that divided a field that is not minor units would invent money."
      >
        <DataTable
          columns={FAKE_COLUMNS}
          rows={FAKE_ROWS}
          caption="3 on this page, last page. The count is this page, never the collection."
        />
      </Demo>

      <Demo
        title="DataTable with nothing in it"
        why="A table that renders its head over no body claims a query ran and found the columns interesting. It renders the empty sentence instead."
      >
        <DataTable columns={FAKE_COLUMNS} rows={[]} tagged="payroll" />
      </Demo>

      <Demo
        title="Permission — why you are seeing what you are seeing"
        why="Three states, three words, three colours: permitted, held but never declared by this app, and declared by this app but not held by you. The third is the one people report as a bug."
      >
        <Permission
          groups={["customer", "vendor", "finance"]}
          effective={{
            permitted: ["customer", "vendor"],
            declared: ["customer", "vendor", "employee"],
          }}
        />
      </Demo>

      <Demo
        title="Permission on a screen tags do not govern"
        why="It says what does govern the screen rather than rendering an empty intersection, which reads as 'permitted nothing'."
      >
        <Permission
          groups={["customer", "vendor"]}
          effective={null}
          gate="Governed by having a manifest, not by tags. This lists declarations, not records."
        />
      </Demo>

      <Demo
        title="RecordGraph — one record and its one hop out"
        why="The invoices spoke is drawn refused rather than omitted: a diagram missing a piece with no sign a piece is missing is worse than no diagram, because it is believed. Everything in the picture is repeated as a list underneath, because an SVG is not keyboard-navigable."
      >
        <RecordGraph graph={FAKE_GRAPH} />
      </Demo>

      <Demo
        title="Provenance — who wrote this, and was anybody there"
        why="Three states for mode, not two. 'Not recorded' is honest about a row that predates the field rather than a guess dressed as a null."
      >
        <Provenance row={FAKE_RECORD} fields={FAKE_FIELDS} />
      </Demo>

      <Demo
        title="ReachableBy — which apps could reach this record"
        why="The app half only, and the caption says so. 'Three apps can read this' when the truth is 'three apps could, for a user who holds the tags' is exactly the overstatement this product exists to prevent."
      >
        <ReachableBy apps={FAKE_APPS} tags={FAKE_RECORD.tags} />
      </Demo>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// What the MCP serves
// ---------------------------------------------------------------------------

/**
 * The same rules, as the agent-facing server hands them over.
 *
 * The payload below is built here from the *same* parsed rules this screen
 * renders, in the shape `mcp/lopie_mcp/tools.py` returns. It is not a
 * recording of a response and it is not a call: the console cannot call that
 * server from a browser, and saying so is better than a screen implying a
 * live link it does not have.
 *
 * The button attempts the call anyway, because the honest way to state a
 * limitation is to let somebody watch it happen.
 */
function Mcp() {
  const url = hostConfig().mcp || "";
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);

  const check = useCallback(async () => {
    setProbing(true);
    setProbe(null);
    try {
      const response = await fetch(`${url}/`, { cache: "no-store" });
      const body = await response.json();
      setProbe({ ok: true, body });
    } catch (e) {
      setProbe({ ok: false, message: e.message });
    } finally {
      setProbing(false);
    }
  }, [url]);

  // The shape `design_rules` returns, rebuilt from the same source it reads.
  // Not a fixture: change the README and both this and the server change.
  const payload = useMemo(
    () => ({
      source: SOURCE,
      rules: RULES.map((rule) => ({
        rule: rule.rule,
        enforcement: {
          by: rule.enforcement.by,
          detail: rule.enforcement.detail,
          unlabelled: rule.enforcement.unlabelled,
        },
        why: `${rule.why.slice(0, 140)}…`,
      })),
      enforcement_categories: Object.fromEntries(
        ORDER.map((id) => [id, CATEGORIES[id].detail]),
      ),
      enforcement_summary: {
        total: SUMMARY.total,
        kernel: SUMMARY.kernel,
        test: SUMMARY.test,
        review: SUMMARY.review,
        review_only: SUMMARY.reviewOnly,
      },
    }),
    [],
  );

  return (
    <Stack spacing={2}>
      <Panel
        title="An agent building an app is told the same thing"
        caption={
          "mcp.lopie.one is a build-time MCP server. Its design_rules tool " +
          "parses packages/kernel-ui/README.md — the file this screen parses " +
          "— so a rule added once reaches a developer reading this page and " +
          "an agent scaffolding a micro-app, with no change to either."
        }
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          <strong>This screen does not call that server.</strong> Two reasons,
          and both are real: <code>{url || "the MCP"}</code> answers with no{" "}
          <code>Access-Control-Allow-Origin</code>, so a browser on this origin
          cannot read the response; and the tools themselves need a bearer
          token from that server&apos;s own OAuth flow, which is a different
          flow from the one that signed you into this console — deliberately,
          for the same reason a remote signs in as itself.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          So what is below is rendered from the shared source, in the shape the
          tool returns. It is what the server would serve, not a recording of
          what it did. You can see the real thing by opening{" "}
          <a href={url} target="_blank" rel="noreferrer">
            {url}
          </a>{" "}
          in a tab, or by pointing a client at it:
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: "background.default",
            border: 1,
            borderColor: "divider",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.75rem",
            overflowX: "auto",
          }}
        >
          {`claude mcp add --transport http lopie-one ${url}/mcp`}
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2, alignItems: "center", flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={check} disabled={probing}>
            {probing ? "Trying…" : "Try it from this page anyway"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            The honest way to state a limitation is to let somebody watch it
            happen.
          </Typography>
        </Stack>

        {probe && !probe.ok ? (
          <ErrorState
            sx={{ mt: 1.5 }}
            severity="info"
            title="Blocked, as expected"
            failure={{ message: probe.message }}
          >
            <Typography variant="body2" sx={{ mt: 1 }}>
              The browser refused to hand this page the response. That is CORS
              working, not the server being down — the same request from a
              terminal returns 200.
            </Typography>
          </ErrorState>
        ) : null}

        {probe?.ok ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              It answered. The server now sends CORS headers, so this page can
              read its front door after all — the sentence above is out of
              date and should be corrected rather than left standing.
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0, p: 1.5, borderRadius: 1,
                bgcolor: "background.default",
                border: 1, borderColor: "divider",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                fontSize: "0.75rem", overflowX: "auto",
              }}
            >
              {JSON.stringify(probe.body, null, 2)}
            </Box>
          </Box>
        ) : null}
      </Panel>

      <Panel
        title="design_rules"
        caption="Rendered from packages/kernel-ui/README.md, in the shape mcp/lopie_mcp/tools.py returns it. The reasoning is truncated here and whole there."
      >
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: "background.default",
            border: 1,
            borderColor: "divider",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: "0.75rem",
            // A JSON payload is as wide as its longest line, and the page body
            // never scrolls sideways.
            overflowX: "auto",
            maxHeight: 520,
            overflowY: "auto",
          }}
        >
          {JSON.stringify(payload, null, 2)}
        </Box>
      </Panel>

      <Panel
        title="What it will not tell an agent"
        caption="Stated here because a screen about what a server serves should be as clear about what it does not."
      >
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            <strong>It reads no business object.</strong>{" "}
            <code>manifests/mcp.json</code> declares no reads and one action,{" "}
            <code>manifest.request</code>. An agent that adds this server gains
            no access to any record in this kernel.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            <strong>It cannot approve its own request.</strong>{" "}
            <code>manifest.review</code> is deliberately not declared: the app
            that helps you ask cannot be the app that decides.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            <strong>It does not enforce any of this.</strong> Serving a rule to
            an agent is not the same as an agent following it — which is the
            whole reason the labels on the Rules tab exist, and why most of
            them say <em>review</em>.
          </Typography>
        </Stack>
      </Panel>
    </Stack>
  );
}

// ---------------------------------------------------------------------------

export default function Design({ onEffective }) {
  const [tab, setTab] = useState("rules");

  useEffect(() => {
    // Nothing on this screen is read from the kernel, so there is no
    // intersection to report. Saying so beats leaving the strip showing
    // whatever the last section resolved, which would attach a permission to
    // a page that has none.
    onEffective(null);
  }, [onEffective]);

  return (
    <Stack spacing={2}>
      <Tabs
        value={tab}
        onChange={(_, next) => setTab(next)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        {TABS.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} sx={{ minHeight: 40 }} />
        ))}
      </Tabs>

      {tab === "rules" ? <Rules /> : null}
      {tab === "brand" ? <Brand /> : null}
      {tab === "components" ? <Components /> : null}
      {tab === "mcp" ? <Mcp /> : null}
    </Stack>
  );
}
